import { useState } from "react";
import { Mui } from "@osu/react-ui";
import Choices from "./Choices";
import { mergeSxStyles } from "../../../../../util/functions";

const { FormHelperText, FormControl } =
  Mui;

/**
 * Radio input component using Material UI Radio from @osu/react-ui
 * @param {Object} props - Component props
 * @param {string} props.id - Input identifier
 * @param {string} props.label - Input label
 * @param {string} props.description - Input description
 * @param {Array} props.choices - Array of choice objects with label and value
 * @param {function} props.onSelectionChange - Callback when selection changes
 * @param {boolean} props.required - Whether the field is required
 * @param {string} props.helperText - Helper text to display below the input
 * @param {boolean} props.error - Whether to display the input in an error state
 */

const RadioInputGroup = (props = {}) => {
  const {
    id,
    label,
    required,
    error = undefined,
    description,
    radioGroupProps: _radioGroupProps = {},
    choices = [],
    helperText,
    name,
    defaultValue,
    onChange: _onChange,
    setValue,
    value: _value,
    readOnly,
    ...rest
  } = props

  const [selected, setSelected] = useState(defaultValue ?? "");
  const radioGroupProps = {
    ..._radioGroupProps,
    name,
    defaultValue,
  };
  if (_onChange || setValue) {
    radioGroupProps.value = _value;
    radioGroupProps.onChange = (event, newVal) => {
      setValue && setValue(newVal);
      _onChange && _onChange(event, newVal);
    };
  } else {
    radioGroupProps.value = selected;
    radioGroupProps.onChange = (event, newVal) => {
      _onChange && _onChange(event, newVal);
      setSelected(newVal);
    };
  }

  const labelId = label && id && id + "-radio-group-label";
  const describedbyId = description && id && id + "-radio-group-description";

  if (labelId) {
    radioGroupProps["aria-labelledby"] = labelId;
  }
  if (describedbyId) {
    radioGroupProps["aria-describedby"] = describedbyId;
  }

  return (
    <FormControl {...rest} sx={mergeSxStyles(rest?.sx, {
      display: 'flex'
    })}>
      {labelId && (
        <Mui.FormLabel
          variant="body1"
          component="label"
          id={labelId}
          required={required}
          error={error}
        >
          {label}
        </Mui.FormLabel>
      )}
      {describedbyId && (
        <Mui.Typography
          id={describedbyId}
          variant="body2"
          gutterBottom
          component="div"
        >
          {description}
        </Mui.Typography>
      )}
      <Choices
        {...radioGroupProps}
        choices={choices}
        readOnly={readOnly}
        required={required}
      />
      {helperText && <FormHelperText component="span" sx={{
        display: 'flex',
        alignItems: 'center',
        lineHeight: 'normal'
      }} error={error}>{helperText}</FormHelperText>}
    </FormControl>
  );
};

export default RadioInputGroup
